import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationKey } from '../../i18n/translationKeys';
import { eventHistoryService } from '../../services/eventHistoryService';
import type { HistoryEventItemDto } from '../../services/eventHistoryService';
import { HistoryTimelineSkeleton } from './HistoryTimelineSkeleton';
import { HistoryRow } from './HistoryRow';
import { HistoryGroup } from './HistoryGroup';
import { groupHistoryEvents, type HistoryGrouping } from './groupHistoryEvents';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { SkeletonBar } from '../../ui/Skeleton/SkeletonBar';

const PAGE_SIZE = 20;

interface HistoryTimelineProps {
    /** When set, the timeline is scoped to a single customer; otherwise it shows the global history. */
    customerId?: string;
    /** Reports the total number of history entries whenever the first page (re)loads — e.g. to drive a tab badge. */
    onCountChange?: (count: number) => void;
    /** Heading granularity: a customer's history is short enough to group by year. Defaults to 'month'. */
    groupBy?: HistoryGrouping;
}

export const HistoryTimeline: React.FC<HistoryTimelineProps> = ({ customerId, onCountChange, groupBy = 'month' }) => {
    const { t, i18n } = useTranslation();

    const [events, setEvents] = useState<HistoryEventItemDto[]>([]);
    const [page, setPage] = useState(2);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const sentinelRef = useRef<HTMLDivElement>(null);

    // Hold the latest onCountChange so the fetch effects can report the count without
    // listing it as a dependency — a caller may pass an inline callback without it
    // retriggering a reload.
    const onCountChangeRef = useRef(onCountChange);
    useEffect(() => { onCountChangeRef.current = onCountChange; });

    useEffect(() => {
        setLoading(true);
        setError(null);
        setExpandedId(null);
        eventHistoryService.getHistory({ customerId, page: 1, pageSize: PAGE_SIZE })
            .then((data) => {
                setEvents(data.items);
                setHasMore(data.totalCount > PAGE_SIZE);
                setPage(2);
                onCountChangeRef.current?.(data.totalCount);
            })
            .catch(() => setError(t(TranslationKey.History.Error)))
            .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const loadNextPage = useCallback(() => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        setLoadMoreError(null);
        eventHistoryService.getHistory({ customerId, page, pageSize: PAGE_SIZE })
            .then((data) => {
                setEvents((prev) => [...prev, ...data.items]);
                setHasMore(events.length + data.items.length < data.totalCount);
                setPage((p) => p + 1);
            })
            .catch(() => setLoadMoreError(t(TranslationKey.History.LoadMoreError)))
            .finally(() => setLoadingMore(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, page, hasMore, loadingMore, events.length]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) loadNextPage(); },
            { threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [loadNextPage]);

    const handleToggle = (id: string) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    const reloadFirstPage = useCallback(() => {
        eventHistoryService.getHistory({ customerId, page: 1, pageSize: PAGE_SIZE })
            .then((data) => {
                setEvents(data.items);
                setHasMore(data.totalCount > PAGE_SIZE);
                setPage(2);
                setExpandedId(null);
                onCountChangeRef.current?.(data.totalCount);
            })
            .catch(() => setError(t(TranslationKey.History.Error)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const groups = groupHistoryEvents(events, groupBy, i18n.language);

    return (
        <div className="flex flex-col gap-l" data-testid="history-list">
            {loading && <HistoryTimelineSkeleton />}

            {!loading && error && (
                <div className="flex flex-col items-start gap-s">
                    <p className="text-s text-danger" data-testid="history-error">{error}</p>
                    <LinkButton
                        onClick={() => { setLoading(true); setError(null); }}
                        data-testid="history-retry-btn"
                    >
                        {t(TranslationKey.History.Retry)}
                    </LinkButton>
                </div>
            )}

            {!loading && !error && (
                <>
                    {events.length === 0 ? (
                        <p className="text-s text-primary-700" data-testid="history-empty-state">
                            {customerId ? t(TranslationKey.History.EmptyClient) : t(TranslationKey.History.Empty)}
                        </p>
                    ) : (
                        <div className="flex flex-col gap-2xl">
                            {groups.map((group) => (
                                <HistoryGroup
                                    key={group.key}
                                    groupKey={group.key}
                                    label={group.label}
                                    headingTestId={`history-${groupBy}-heading-${group.key}`}
                                    collapsible={!customerId}
                                >
                                    {group.events.map(event => (
                                        <HistoryRow
                                            key={event.id}
                                            event={event}
                                            locale={i18n.language}
                                            isExpanded={expandedId === event.id}
                                            onToggle={() => handleToggle(event.id)}
                                            onReactivated={reloadFirstPage}
                                        />
                                    ))}
                                </HistoryGroup>
                            ))}
                        </div>
                    )}

                    <div ref={sentinelRef} className="h-1" data-testid="history-sentinel" />

                    {loadingMore && (
                        <SkeletonBar className="h-10" />
                    )}

                    {loadMoreError && (
                        <div className="flex items-center gap-m">
                            <p className="text-s text-danger">{loadMoreError}</p>
                            <LinkButton onClick={loadNextPage}>
                                {t(TranslationKey.History.Retry)}
                            </LinkButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
